import { Hero } from "@/components/sections/Hero";
import { ServicesGrid } from "@/components/sections/ServicesGrid";
import { BezirkeGrid } from "@/components/sections/BezirkeGrid";
import { ProblemsGrid } from "@/components/sections/ProblemsGrid";
import { WhyUs } from "@/components/sections/WhyUs";
import { Reviews } from "@/components/sections/Reviews";
import { FAQ } from "@/components/sections/FAQ";
import { ContactSection } from "@/components/sections/ContactSection";
import { SEO } from "@/components/SEO";
import { useLang } from "@/i18n/LanguageContext";
import { site } from "@/config/site";

const faqsDe = [
  { q: "Wie schnell ist der Schlüsseldienst in Wien vor Ort?", a: "In den meisten Wiener Bezirken sind wir in 15–20 Minuten bei Ihnen. In den Außenbezirken (Floridsdorf, Donaustadt, Liesing) kann es bis zu 30 Minuten dauern." },
  { q: "Was kostet eine Türöffnung in Wien?", a: "Eine Standard-Türöffnung kostet ab 79 € werktags. Sie erhalten den verbindlichen Festpreis vor Anfahrt – ohne versteckte Kosten." },
  { q: "Sind Sie wirklich 24 Stunden erreichbar?", a: "Ja, unser Notdienst ist 24 Stunden, 7 Tage die Woche – auch sonntags und an Feiertagen – telefonisch erreichbar." },
  { q: "Beschädigen Sie das Schloss bei der Öffnung?", a: "In 95 % der Fälle öffnen unsere Techniker Türen schadenfrei mit professionellem Pick-Werkzeug." },
  { q: "Welche Bezirke deckt Ihr Service ab?", a: "Wir sind in allen 23 Wiener Bezirken aktiv – von 1010 Innere Stadt bis 1230 Liesing." },
];
const faqsEn = [
  { q: "How fast does the locksmith arrive in Vienna?", a: "In most Vienna districts we arrive within 15–20 minutes. In outer districts (Floridsdorf, Donaustadt, Liesing) it can take up to 30 minutes." },
  { q: "What does a door opening in Vienna cost?", a: "A standard door opening starts at €79 on weekdays. You receive a binding fixed price before dispatch – no hidden costs." },
  { q: "Are you really available 24 hours?", a: "Yes, our emergency service is reachable 24 hours, 7 days a week – including Sundays and holidays." },
  { q: "Do you damage the lock when opening?", a: "In 95% of cases our technicians open doors damage-free using professional picking tools." },
  { q: "Which districts do you cover?", a: "We operate in all 23 Vienna districts – from 1010 Innere Stadt to 1230 Liesing." },
];

const Home = () => {
  const { lang } = useLang();
  const faqs = lang === "de" ? faqsDe : faqsEn;

  const title = lang === "de"
    ? "Schlüsseldienst Wien 24h – Türöffnung ab 79 € | Notdienst"
    : "Locksmith Vienna 24/7 – Door Opening from €79 | Emergency";
  const description = lang === "de"
    ? "Schlüsseldienst Wien 24h Notdienst. Türöffnung ab 79 €, schadenfrei, in 15 Min vor Ort. Geprüft & versichert. In allen 23 Bezirken. Jetzt anrufen!"
    : "24/7 locksmith Vienna. Door opening from €79, damage-free, on-site in 15 min. Certified & insured. All 23 districts. Call now!";

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "Locksmith",
    name: site.name,
    image: site.url + "/og.jpg",
    "@id": site.url,
    url: site.url,
    telephone: site.phone,
    priceRange: site.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: site.street,
      postalCode: site.postalCode,
      addressLocality: site.city,
      addressCountry: site.country,
    },
    geo: { "@type": "GeoCoordinates", latitude: 48.2082, longitude: 16.3738 },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      opens: "00:00", closes: "23:59",
    }],
    aggregateRating: { "@type": "AggregateRating", ratingValue: site.rating, reviewCount: site.reviewCount },
    areaServed: { "@type": "City", name: "Wien" },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO title={title} description={description} path="/" jsonLd={[localBusiness, faqLd] as unknown as object} />
      <Hero />
      <ServicesGrid />
      <ProblemsGrid />
      <BezirkeGrid />
      <WhyUs />
      <Reviews />
      <FAQ items={faqs} />
      <ContactSection />
    </>
  );
};

export default Home;